import { Breakpoints, useMediaQuery } from '@leland-dev/leland-ui-library';
import React, { type FC, useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { type Scalars } from '../../../../__generated-gql-types__/globalTypes';
import { Textarea } from '../../../forms/Inputs';
import {
  type ScheduleData,
  type ScheduleModalStep,
  type SessionOption,
} from '../ScheduleModal';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import { type ScheduleMessageModalCoachFragment } from './__generated-gql-types__/ScheduleMessageModal.generated';
import { type SchedulingSuccessModalCreateCoachingSessionByApplicantMutationVariables } from './__generated-gql-types__/SchedulingSuccessModal.generated';

interface ScheduleMessageModalProps
  extends BaseScheduleSubmissionModalLayoutProps {
  coach: ScheduleMessageModalCoachFragment;
  selectedTime: Possible<number>;
  sessionLength: number;
  setOpen: (open: boolean) => void;
  setSelectedTime: (time: Possible<number>) => void;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  coachingSessionUrn: Possible<Scalars['CoachingSessionUrn']['output']>;
  setStep: (step: ScheduleModalStep) => void;
  sessionType: SessionOption;
  offeringPackageIds: string[];
  isPayAsYouGo: boolean;
  orderToAssign?: string;
  justThisSession: boolean;
  onMessageCollected: (string, ScheduleData) => void;
}

export const ScheduleMessageModal: FC<ScheduleMessageModalProps> = ({
  categoryUrn,
  coach,
  coachingSessionUrn,
  isPayAsYouGo,
  offeringPackageIds,
  selectedTime,
  sessionLength,
  sessionType,
  setOpen,
  setSelectedTime,
  setStep,
  orderToAssign,
  recurringCadence,
  justThisSession,
  onMessageCollected,
  ...baseScheduleModalProps
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);

  const isRescheduling = !!coachingSessionUrn;

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<SchedulingSuccessModalCreateCoachingSessionByApplicantMutationVariables>();

  const onSubmitWrapper: SubmitHandler<
    SchedulingSuccessModalCreateCoachingSessionByApplicantMutationVariables
  > = ({ message }) => {
    const collectedSchedulingData: ScheduleData = {
      selectedTime,
      sessionLength,
      sessionType,
      coach,
      categoryUrn,
      offeringPackageIds,
      isPayAsYouGo,
      orderToAssign: orderToAssign ?? null,
      justThisSession: justThisSession ?? true,
      recurringCadence,
      message,
    };

    onMessageCollected(message, collectedSchedulingData);
  };

  /* 
    This is a workaround for mobile safari to prevent the page from scrolling after the virtual keyboard is opened. 
    On mobile it will essentially override scroll to keep under scroll from happening.
    When the component unmounts we remove the listeners.
  */
  useEffect(() => {
    if (isMd) {
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const visualViewport = window.visualViewport;
    let offset = 0;
    let viewportWidth = window.innerWidth;
    let viewportHeight = window.innerHeight;

    const handleScrollToTop = () => {
      window.scrollTo(0, 0);
    };

    const blurActiveElement = () => {
      const activeElement = document.activeElement as HTMLInputElement;
      if (activeElement) {
        try {
          activeElement.blur();
        } catch (e) {
          // this is a chance active element somehow gets set to a non-input element so calling blur will throw.
          console.error(e);
        }
      }
    };

    const handleResize = (event: Event) => {
      const $target = event?.target as unknown as HTMLDivElement;
      const $page = document.body;

      if (viewportWidth !== parseInt($target.style.width)) {
        viewportWidth = window.innerWidth;
        viewportHeight = window.innerHeight;
      }

      if (viewportHeight - parseInt($target.style.height) > 150) {
        handleScrollToTop();
        const adjustment =
          viewportHeight - parseInt($target.style.height) - offset;
        $page.style.bottom = `${adjustment}px`;
      } else if (
        viewportHeight === parseInt($target.style.height) ||
        viewportHeight - parseInt($target.style.height) <= 150
      ) {
        offset = viewportHeight - parseInt($target.style.height);
        $page.style.bottom = '0px';
      }
    };

    // only do this if it's mobile size
    // Check for visual viewport to handle resizing
    if (visualViewport) {
      visualViewport.addEventListener('resize', handleResize, { signal });
    }

    document.addEventListener('touchend', handleScrollToTop, { signal });
    document.addEventListener('touchmove', blurActiveElement, { signal });

    return () => abortController.abort();
  }, [isMd]);

  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        type: 'submit',
        form: 'schedule_message_modal_form',
        label: selectedTime
          ? isRescheduling
            ? 'Reschedule'
            : 'Schedule'
          : 'Send Message',
        disabled: isSubmitting,
      }}
      sidebarOptions={{
        title: 'You’re almost done!',
        subtitle:
          'Make sure everything looks right before sending your request!',
      }}
      coach={coach}
      selectedTime={selectedTime}
      sessionLength={sessionLength}
      setSelectedTime={setSelectedTime}
      sessionType={sessionType}
      preventScroll
      recurringCadence={recurringCadence}
      {...baseScheduleModalProps}
    >
      <div className="flex w-full grow flex-col overflow-scroll overscroll-contain p-6 md:pb-40">
        {selectedTime ? (
          <>
            <h4 className="text-2xl font-medium text-leland-gray-dark">
              <>
                Add a message{' '}
                {isRescheduling ? (
                  <span className="text-base text-leland-gray-light">
                    (Optional)
                  </span>
                ) : null}
              </>
            </h4>
            <p className="mt-2 max-w-xl text-lg text-leland-gray-light">
              Including a personal message helps the coach know how serious you
              are. Coaches are more likely to accept an intro call if it comes
              with a message.
            </p>
          </>
        ) : (
          <>
            <h4 className="text-2xl font-medium text-leland-gray-dark">
              Send {coach.user.firstName} a message
            </h4>
          </>
        )}
        <div className="mt-6 flex w-full flex-col">
          <form
            id="schedule_message_modal_form"
            onSubmit={handleSubmit(onSubmitWrapper)}
          >
            <Textarea
              placeholder={`Hi ${coach.user.firstName}! I'm interested in purchasing coaching and would love to get to know you and learn about your coaching offerings. My goal is...`}
              label={
                <>
                  <span className="text-leland-gray-dark">
                    Include a message
                  </span>{' '}
                  <span className="ml-1 text-leland-gray-extra-light">
                    Optional
                  </span>
                </>
              }
              labelIsHidden={true}
              register={register('message')}
              rows={4}
            />
          </form>
        </div>
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};
